
import { NavLink } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { features } from '../lib/features';

const navItems = [
  { to: '/queue',    label: 'Queue',    short: 'Q' },
  { to: '/library',  label: 'Library',  short: 'L' },
  ...(features.discover ? [{ to: '/discover', label: 'Discover', short: 'D' }] : []),
  { to: '/settings', label: 'Settings', short: 'S' },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const { isAdmin, signOut } = useAuth();

  return (
    <aside
      className="hidden lg:flex flex-col min-h-screen border-r border-border-subtle bg-surface-0 flex-shrink-0 transition-all duration-200"
      style={{ width: collapsed ? 56 : 200 }}
    >
      {/* Logo + collapse toggle */}
      <div className="flex items-center justify-between px-3 py-5 border-b border-border-subtle" style={{ minHeight: 60 }}>
        {!collapsed && (
          <span className="font-bold text-lg tracking-tight" style={{
            background: 'linear-gradient(135deg, #8B5CF6, #06B6D4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>grove</span>
        )}
        <button
          onClick={onToggle}
          className="w-7 h-7 flex items-center justify-center rounded-md transition-colors hover:bg-white/5 text-sm text-text-muted"
          style={{ marginLeft: collapsed ? 'auto' : 0, marginRight: collapsed ? 'auto' : 0 }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 flex flex-col gap-0.5 px-2 pt-3">
        {navItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-lg text-sm font-medium transition-colors ${
                collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
              } ${
                isActive
                  ? 'bg-violet-600/15 text-violet-300'
                  : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'
              }`
            }
          >
            {collapsed ? (
              <span className="text-xs font-bold">{item.short}</span>
            ) : (
              item.label
            )}
          </NavLink>
        ))}
        {isAdmin && (
          <NavLink
            to="/admin"
            title={collapsed ? 'Admin' : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-lg text-sm font-medium transition-colors ${
                collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
              } ${
                isActive ? 'bg-violet-600/15 text-violet-300' : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'
              }`
            }
          >
            {collapsed ? <span className="text-xs font-bold">A</span> : 'Admin'}
          </NavLink>
        )}
      </nav>

      {/* Sign out */}
      <button
        onClick={() => signOut()}
        title={collapsed ? 'Sign out' : undefined}
        className={`flex items-center rounded-lg text-sm text-gray-600 hover:text-gray-300 hover:bg-white/5 transition-colors mb-4 mx-2 ${
          collapsed ? 'justify-center py-2.5' : 'gap-3 px-3 py-2'
        }`}
      >
        <LogOut size={16} />
        {!collapsed && <span>Sign out</span>}
      </button>
    </aside>
  );
}
